import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from './utils';

/**
 * Badge — styled per Pelipost UX/UI Style Guide §12.
 *
 * Status variants are pill-shaped (rounded-full), 12px font, 600 weight:
 *   delivered    success-light bg, success text, success border
 *   inTransit    info-light bg, info text, info border
 *   processing   warning-light bg, warning text, warning border
 *   returned     error-light bg, error text, error border
 *
 * Neutral variants for non-status uses:
 *   brand        brand-light bg, brand text (for avatar-adjacent labels, tags)
 *   cta          cta-light bg, cta text (highlight/accent tags)
 *   outline      transparent bg, border, text color
 */
const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border text-xs font-semibold px-3 py-1 w-fit whitespace-nowrap shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none',
  {
    variants: {
      variant: {
        delivered: 'bg-success-light text-success border-success',
        inTransit: 'bg-info-light text-info border-info',
        processing: 'bg-warning-light text-warning border-warning',
        returned: 'bg-error-light text-error border-error',
        brand: 'bg-brand-light text-brand border-transparent',
        cta: 'bg-cta-light text-cta border-transparent',
        outline: 'bg-transparent text-text border-border',
      },
    },
    defaultVariants: {
      variant: 'brand',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
